//path: src\hooks\AuthProvider.tsx

import { FC, useEffect, useState, useCallback } from 'react';
import { BackendClient, BackendUser as User, BackendSession as Session, OAUTH_PROVIDER_KEY, OAUTH_OUTCOME_KEY, OAUTH_MARKETING_CONSENT_KEY } from '@/infrastructure/clients/BackendClient';
import { setUserContext, clearUserContext, captureError } from '@/utils/sentry';
import { setNewRelicUser } from '@/modules/telemetry/newRelicBrowser';
import { sessionChanged, sessionOpening } from '@/infrastructure/api/v1/client';
import { leaveAuthNotice } from '@/modules/auth/authNotice';
import { isSignUpOpen } from './environmentPreferencesRead';
import { AuthContext } from './authContext';

const signInWith = (provider: string | null) => (provider === 'apple' ? 'Sign-in with Apple' : provider === 'google' ? 'Sign-in with Google' : 'Sign-in');

function takeOAuthStart(): { provider: string | null; marketingConsent: boolean } {
  try {
    const provider = sessionStorage.getItem(OAUTH_PROVIDER_KEY);
    const marketingConsent = sessionStorage.getItem(OAUTH_MARKETING_CONSENT_KEY) === 'true';
    sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
    sessionStorage.removeItem(OAUTH_MARKETING_CONSENT_KEY);
    return { provider, marketingConsent };
  } catch {
    return { provider: null, marketingConsent: false };
  }
}

// After an OAuth sign-in, the backend middleman bounces the browser back to this
// origin with the session in the URL fragment (#access_token=…&refresh_token=…),
// or a refusal (#error=…). Persist the session so it is live on landing, or leave
// the sign-in page a message saying why it is not. Recovery links (#…&type=recovery)
// are intentionally left for PasswordResetConfirm to handle.
async function consumeOAuthRedirect(): Promise<void> {
  if (typeof window === 'undefined') return;
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw) return;
  const params = new URLSearchParams(raw);
  if (params.get('type') === 'recovery') return;
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  const oauthError = params.get('error') ?? params.get('error_code');
  if (!oauthError && (!access_token || !refresh_token)) return;

  // Out of the address bar and history before anything is awaited
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  const { provider, marketingConsent } = takeOAuthStart();

  if (oauthError || !access_token || !refresh_token) {
    leaveAuthNotice({
      tone: 'error',
      message: params.get('error') === 'access_denied'
        ? `${signInWith(provider)} was cancelled or refused. Please try again.`
        : `${signInWith(provider)} did not finish. Please try again.`,
    });
    return;
  }

  try {
    const { data, error } = await BackendClient.auth.setSession({ access_token, refresh_token }, { mayCreateOrg: isSignUpOpen });
    if (error) {
      leaveAuthNotice({
        tone: 'error',
        message: error.code === 'sign_up_closed'
          ? "We're not open to new accounts here yet. Join the waitlist from the sign-up page."
          : `${signInWith(provider)} did not finish. ${error.code === 'rate_limited' ? error.message : 'Please try again.'}`,
      });
      if (error.code !== 'sign_up_closed') captureError(new Error(`OAuth return failed: ${error.code}`), { domain: 'auth', operation: 'oauthRedirectConsume' });
      return;
    }
    // Hand the analytics layer (OAuthAnalyticsTracker, below ServiceProvider) what it
    // needs to fire the sign-up/login event for this OAuth return. AuthProvider sits
    // above ServiceProvider so it can't emit analytics itself. One-shot, consumed once.
    try {
      sessionStorage.setItem(OAUTH_OUTCOME_KEY, JSON.stringify({
        provider: provider || 'oauth',
        isNewUser: data.session?.user?.is_new_user === true,
        marketingConsent,
      }));
    } catch { /* best-effort: analytics handoff is non-critical */ }
  } catch (err) {
    captureError(err, { domain: 'auth', operation: 'oauthRedirectConsume' });
    leaveAuthNotice({ tone: 'error', message: `${signInWith(provider)} did not finish. Please try again.` });
  }
}

// Once a page load: the fragment is gone after the first read, so a second mount (StrictMode) waits on the same return
let oauthReturn: Promise<void> | null = null;
const consumeOAuthRedirectOnce = () => (oauthReturn ??= consumeOAuthRedirect());

// The same person as before: kept as the same object, so a token refresh does not look like a new user to the pages
function sameUser(a: User | null, b: User): boolean {
  return Boolean(a)
    && a!.id === b.id
    && a!.email === b.email
    && a!.is_new_user === b.is_new_user
    && JSON.stringify(a!.user_metadata ?? null) === JSON.stringify(b.user_metadata ?? null);
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const showUser = useCallback((next: User | null) => {
    setUser(prev => (next && sameUser(prev, next) ? prev : next));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data: { user: updatedUser } } = await BackendClient.auth.getUser();
      if (updatedUser) {
        showUser(updatedUser);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  }, [showUser]);

  useEffect(() => {
    async function fetchSession() {
      // Persist a freshly-bounced OAuth session before reading it back.
      await consumeOAuthRedirectOnce();
      const response = await BackendClient.auth.getSession();
      const session = response.data.session;
      if (!response.error && session?.user?.id) {
        setSession(session);
        showUser(session.user);
        setUserContext({ id: session.user.id, email: session.user.email });
        setNewRelicUser(session.user.id);
      } else {
        setSession(null);
        setUser(null);
        clearUserContext();
        setNewRelicUser(null);
      }
      setLoading(false);
    }

    fetchSession();

    const { data: authListener } = BackendClient.auth.onAuthStateChange(
      (event, session) => {
        // fetchSession reads the first session itself, once any OAuth return is stored
        if (event === 'INITIAL_SESSION') return;
        // Another tab signed in as someone else: everything this tab holds is the previous user's
        if (event === 'USER_CHANGED') {
          window.location.replace('/dashboard');
          return;
        }
        // Another user, or none: nothing read or saved for the last one is shown to or sent as this one
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') sessionChanged();
        // Signing in starts the app for this user: what it reads first is one read of the session bootstrap
        if (event === 'SIGNED_IN') sessionOpening();
        if (session?.user?.id) {
          setSession(session);
          showUser(session.user);
          setUserContext({ id: session.user.id, email: session.user.email });
          setNewRelicUser(session.user.id);
        } else {
          setSession(null);
          setUser(null);
          clearUserContext();
          setNewRelicUser(null);
        }
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [showUser]);

  return (
    <AuthContext.Provider value={{ user, session, loading, refreshUser }}>
      {props.children}
    </AuthContext.Provider>
  );
};
